import "./wowza.scss";
import { ReactNode, useEffect, useRef } from "react";
import { loadScript } from "design-system/hooks/use-script";
import { MediaAssetImageProps } from "../media-asset/media-asset-image";
import { useDataLayer } from "frontend/hooks/use-data-layer";

export interface WowzaEmbedProps {
  videoPath: string;
  media?: MediaAssetImageProps;
  caption?: ReactNode;
}

export function WowzaEmbed({ videoPath, media, caption }: WowzaEmbedProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);
  const { pushDataLayer } = useDataLayer();

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) return;

    // push data layer event when video is played
    const handleOnClick = () => {
      pushDataLayer({
        event: "video",
        /* eslint-disable camelcase */
        event_data: {
          video_title: "livestream",
          video_type: "livestream",
        },
      });
    };

    videoElement.addEventListener("play", handleOnClick);

    return () => {
      videoElement.removeEventListener("play", handleOnClick);
    };
  }, [pushDataLayer]);

  // overrideNative forces VHS on Chrome/Edge/Firefox so native HLS doesn't
  // bypass CEA-608 extraction (VJS #9117, #3621). Safari excluded —
  // overrideNative breaks its source loading.
  useEffect(() => {
    if (!videoPath || !videoRef.current) return;

    const videoElement = videoRef.current;
    let active = true;

    loadScript("https://vjs.zencdn.net/7.21.7/video.min.js", {
      onload: () => {
        if (!active) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vjs = (window as any).videojs;
        if (!vjs || !videoElement) return;
        if (!playerRef.current) {
          playerRef.current = vjs(videoElement, {
            suppressNotSupportedError: true,
            html5: {
              vhs: {
                overrideNative: !vjs.browser.IS_SAFARI,
              },
              nativeAudioTracks: false,
              nativeVideoTracks: false,
              nativeTextTracks: false,
            },
          });
        }
        // Set source after player init so VHS intercepts it before Chrome's
        // native HLS player can claim the <source> element at parse time.
        playerRef.current.src({
          src: videoPath,
          type: "application/x-mpegURL",
        });
      },
    });

    return () => {
      active = false;
    };
  }, [videoPath]);

  // Dispose player on unmount only, not on videoPath change — disposing
  // removes the element from the DOM, making reinitialization impossible.
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  // load video.js css
  useEffect(() => {
    const link = document.head.appendChild(
      Object.assign(document.createElement("link"), {
        rel: "stylesheet",
        href: "https://vjs.zencdn.net/7.21.7/video-js.css",
      }),
    );

    // add wowza class to stats topper
    const element = document.querySelector(".hbs-topper.hbs-statistics-topper");
    if (element) {
      element.classList.add("hbs-wowza-embed-topper");
    }

    return () => {
      // cleanup: remove dynamically added link
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div className="hbs-media-asset">
      <video
        ref={videoRef}
        id="hbs-wowza-video"
        className="video-js vjs-big-play-centered"
        controls
        preload="none"
        poster={media?.src || undefined}
      >
        <p className="vjs-no-js">
          To view this video please enable JavaScript, and consider upgrading to
          a web browser that supports HTML5 video.
        </p>
      </video>
      <figcaption>
        <span className="hbs-media-asset__caption-text">
          <div>{caption}</div>
        </span>
      </figcaption>
    </div>
  );
}
